import React, { FC, useEffect, useMemo } from "react";
import Modal from "@/components/Modal";
import Button from "@/components/Button";
import Select from "@/components/Select";
import { useAppTheme } from "@/utils/theme";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  useActions,
  useCreateAction,
  useUpdateAction,
} from "@/hooks/useActions";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useUserStore } from "@/hooks/useUserStore";
import { ErrorResponse } from "@/domain/error.interface";
import { IAction } from "@/domain/actions.interface";
import {
  ACTION_CODE_OPTIONS,
  ACTION_STATUS_OPTIONS,
  ACTION_TYPE_OPTIONS,
} from "@/domain/actions.enum";

type ActionFormData = {
  name: string;
  code: string;
  description: string;
  type?: string;
  link?: string;
  order: number;
  icon?: string;
  subActions: string[];
  status: string;
};

const buildSchema = () =>
  yup.object().shape({
    name: yup.string().required("This field is required"),
    code: yup.string().required("This field is required"),
    description: yup.string().required("This field is required"),
    type: yup.string().nullable().optional(),
    link: yup.string().nullable().optional(),
    order: yup
      .number()
      .typeError("Must be a number")
      .required("This field is required")
      .min(0, "Must be 0 or greater"),
    icon: yup.string().nullable().optional(),
    subActions: yup.array().of(yup.string().required()).default([]),
    status: yup.string().required("This field is required"),
  });

type ActionModalProps = {
  open: boolean;
  onClose: () => void;
  action?: IAction | null;
};

const ActionModal: FC<ActionModalProps> = ({ open, onClose, action }) => {
  const theme = useAppTheme();
  const { showSnackBar } = useFeedBack();
  const { userInfo } = useUserStore();
  const currentUserId = userInfo?._id;

  const isEditMode = !!action?._id;

  const createAction = useCreateAction();
  const updateAction = useUpdateAction();
  const isPending = createAction.isPending || updateAction.isPending;

  const { actions: actionsData, isLoadingActions } = useActions(
    {
      pageNumber: 0,
      pageSize: 500,
      sortField: "name",
      sortOrder: 1,
    },
    open
  );

  const availableActions: IAction[] = useMemo(() => {
    const list = actionsData?.list || [];
    return action?._id ? list.filter((a) => a._id !== action._id) : list;
  }, [actionsData, action]);

  const defaultValues = useMemo<ActionFormData>(() => {
    const subActionsList = Array.isArray(action?.subActions)
      ? (action?.subActions as unknown[]).map((sa) =>
          typeof sa === "string" ? sa : ((sa as IAction)?._id ?? "")
        )
      : [];
    return {
      name: action?.name ?? "",
      code: action?.code ?? "",
      description: action?.description ?? "",
      type: action?.type ?? "",
      link: action?.link ?? "",
      order: typeof action?.order === "number" ? action.order : 0,
      icon: action?.icon ?? "",
      subActions: subActionsList.filter(Boolean),
      status: action?.status ?? "ACTIVE",
    };
  }, [action]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ActionFormData>({
    resolver: yupResolver(buildSchema()) as never,
    mode: "all",
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSubmit = async (data: ActionFormData) => {
    const payload = {
      name: data.name,
      code: data.code,
      status: data.status,
      description: data.description,
      type: data.type || undefined,
      link: data.link || undefined,
      order: Number(data.order),
      icon: data.icon || undefined,
      subActions: data.subActions,
    };

    try {
      if (isEditMode && action?._id) {
        await updateAction.mutateAsync(
          {
            id: action._id,
            data: { ...payload, updatedBy: currentUserId },
          },
          {
            onSuccess: () => {
              showSnackBar("The registry has been saved.", "success");
              onClose();
            },
            onError: (error: ErrorResponse) => {
              showSnackBar(
                (Array.isArray(error.message)
                  ? error.message[0]
                  : error.message) ||
                  error.error ||
                  "An error occurred"
              );
            },
          }
        );
      } else {
        await createAction.mutateAsync(
          { ...payload, createdBy: currentUserId },
          {
            onSuccess: () => {
              showSnackBar("The registry has been saved.", "success");
              onClose();
            },
            onError: (error: ErrorResponse) => {
              showSnackBar(
                (Array.isArray(error.message)
                  ? error.message[0]
                  : error.message) ||
                  error.error ||
                  "An error occurred"
              );
            },
          }
        );
      }
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={isPending}
      title={isEditMode ? "Edit action" : "New action"}
      theme={theme}
      size={{
        width: "100%",
        minHeight: 560,
        maxHeight: 720,
        maxWidth: 760,
      }}
      content={
        <Box sx={{ width: "100%", padding: "20px 0" }}>
          <Grid container spacing={"20px"}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="action-name"
                    label="Name"
                    variant="outlined"
                    required
                    value={field.value || ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    error={!!errors.name}
                    helperText={errors.name?.message || ""}
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="code"
                control={control}
                render={({ field }) => (
                  <Select
                    id="action-code"
                    label="Code"
                    required
                    handleChange={(value) => field.onChange(value as string)}
                    data={ACTION_CODE_OPTIONS}
                    value={field.value || ""}
                    error={!!errors.code}
                    errorMessage={errors.code?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="action-description"
                    label="Description"
                    variant="outlined"
                    required
                    multiline
                    minRows={3}
                    value={field.value || ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    error={!!errors.description}
                    helperText={errors.description?.message || ""}
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <Select
                    id="action-type"
                    label="Type"
                    handleChange={(value) => field.onChange(value as string)}
                    data={ACTION_TYPE_OPTIONS}
                    value={field.value || ""}
                    showClearOption
                    error={!!errors.type}
                    errorMessage={errors.type?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Select
                    id="action-status"
                    label="Status"
                    required
                    handleChange={(value) => field.onChange(value as string)}
                    data={ACTION_STATUS_OPTIONS}
                    value={field.value || ""}
                    error={!!errors.status}
                    errorMessage={errors.status?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="link"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="action-link"
                    label="Link"
                    variant="outlined"
                    value={field.value || ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    error={!!errors.link}
                    helperText={errors.link?.message || ""}
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Controller
                name="order"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="action-order"
                    label="Order"
                    variant="outlined"
                    type="number"
                    required
                    value={field.value ?? 0}
                    onChange={(e) => field.onChange(e.target.value)}
                    error={!!errors.order}
                    helperText={errors.order?.message || ""}
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Controller
                name="icon"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="action-icon"
                    label="Icon"
                    variant="outlined"
                    value={field.value || ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    error={!!errors.icon}
                    helperText={errors.icon?.message || ""}
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <Controller
                name="subActions"
                control={control}
                render={({ field }) => {
                  const selectedIds = field.value || [];
                  const selectedObjects = availableActions.filter((a) =>
                    selectedIds.includes(a._id)
                  );
                  return (
                    <Autocomplete
                      multiple
                      options={availableActions}
                      loading={isLoadingActions}
                      disabled={isPending}
                      getOptionLabel={(option) =>
                        option.name || option._id || ""
                      }
                      isOptionEqualToValue={(option, value) =>
                        option._id === value._id
                      }
                      value={selectedObjects}
                      onChange={(_e, selected) => {
                        field.onChange(selected.map((a) => a._id));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          id="action-subActions"
                          label="Sub actions"
                          placeholder="Select sub actions"
                          error={!!errors.subActions}
                          helperText={
                            (errors.subActions as { message?: string })
                              ?.message || ""
                          }
                        />
                      )}
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
        </Box>
      }
      actions={
        <Box
          sx={{
            display: "flex",
            gap: "10px",
          }}
        >
          <Button
            variantType="cancel"
            onClick={onClose}
            disabled={isPending}
            label="CANCEL"
          />
          <Button
            variantType="save"
            onClick={() => {
              handleSubmit(onSubmit)();
            }}
            disabled={isPending}
            isLoading={isPending}
            label="SAVE"
          />
        </Box>
      }
    />
  );
};

export default ActionModal;
